import {
  Box,
  ClickAwayListener,
  IconButton,
  Popper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InventoryIcon from '@mui/icons-material/Inventory';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import { useTranslation } from 'react-i18next';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import { hasPermissions } from '../../../auth/hasPermissions';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PercentIcon from '@mui/icons-material/Percent';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import { forwardRef, useState } from 'react';
import { tokens } from '../../../global/theme/tokens';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CircularProgress from '@mui/material/CircularProgress';

// "Sort with AI" (ExoPositions::handleAiSort) is wired but not released yet
const SHOW_AI_SORT = false;

const Controls = ({
  cols,
  onNewPosition,
  onSelectProduct,
  onSelectService,
  onNewGroup,
  onNewDiscount,
  onNewPercent,
  onNewHOAI,
  onNewTimeBilling,
  onImport,
  onSortWithAi,
  aiSorting,
  hideServices,
  hideProducts,
  hideNewPosition,
  hideSum,
  hideDiscount,
  hideGroups,
  hideHOAI = true,
  hideTimeBilling = true,
  hidePostPos,
  hideImport,
}) => {
  const { t } = useTranslation();

  const [postPosPopper, setPostPosPopper] = useState(null);

  return (
    <tr>
      <td colSpan={cols.length}>
        <Box
          className="w-full flex flex-row justify-center items-center h-0 mt-4"
          sx={{
            marginBottom: hideSum ? '15px' : '0px',
          }}
        >
          {!hideNewPosition && (
            <Tooltip title={t('Add Custom Position')} arrow>
              <IconButton onClick={() => onNewPosition()}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
          {!hideGroups && (
            <Tooltip title={t('Add Group')} arrow>
              <IconButton onClick={onNewGroup}>
                <GroupWorkIcon />
              </IconButton>
            </Tooltip>
          )}

          {hasPermissions('product', 'read') && !hideProducts && (
            <Tooltip title={t('Add Product Position')} arrow>
              <IconButton onClick={() => onSelectProduct()}>
                <InventoryIcon />
              </IconButton>
            </Tooltip>
          )}

          {hasPermissions('service', 'read') && !hideServices && (
            <Tooltip title={t('Add Service Position')} arrow>
              <IconButton onClick={() => onSelectService()}>
                <SupervisedUserCircleIcon />
              </IconButton>
            </Tooltip>
          )}
          {!hideDiscount && (
            <Tooltip title={t('Add Percentage Position')} arrow>
              <IconButton onClick={() => onNewPercent()}>
                <PercentIcon />
              </IconButton>
            </Tooltip>
          )}
          {!hideTimeBilling && (
            <Tooltip title={t('Time Billing')}>
              <IconButton onClick={() => onNewTimeBilling()}>
                <AccessTimeIcon />
              </IconButton>
            </Tooltip>
          )}
          {!hideHOAI && (
            <Tooltip title={t('Add HOAI')} arrow>
              <IconButton
                onClick={onNewHOAI}
                sx={{
                  maxWidth: '37px',
                  maxHeight: '37px',
                  minWidth: '37px',
                  minHeight: '37px',
                }}
              >
                <Box className="aspect-square flex justify-center items-center">
                  <Typography fontSize={10} fontWeight={800}>
                    HOAI
                  </Typography>
                </Box>
              </IconButton>
            </Tooltip>
          )}
          {!hideImport && (
            <Tooltip title={t('Add positions from another document')} arrow>
              <IconButton
                onClick={() => {
                  onImport();
                }}
                sx={{
                  maxWidth: '37px',
                  maxHeight: '37px',
                  minWidth: '37px',
                  minHeight: '37px',
                }}
              >
                <SaveAltIcon />
              </IconButton>
            </Tooltip>
          )}
          {SHOW_AI_SORT && !hideImport && onSortWithAi && hasPermissions('ai-chat', 'write') && (
            <Tooltip
              title={aiSorting ? t('The AI is sorting the positions...') : t('Sort with AI')}
              arrow
            >
              <span>
                <IconButton
                  onClick={() => onSortWithAi()}
                  disabled={Boolean(aiSorting)}
                  sx={{
                    maxWidth: '37px',
                    maxHeight: '37px',
                    minWidth: '37px',
                    minHeight: '37px',
                  }}
                >
                  {aiSorting ? (
                    <CircularProgress size={18} />
                  ) : (
                    <AutoFixHighIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {!hidePostPos && (
            <Tooltip title={t('Add position after subtotal')} arrow>
              <IconButton
                onClick={(e) => {
                  setPostPosPopper(e.target);
                }}
                sx={{
                  maxWidth: '37px',
                  maxHeight: '37px',
                  minWidth: '37px',
                  minHeight: '37px',
                }}
              >
                <LowPriorityIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Popper
          className="z-[10]"
          open={Boolean(postPosPopper)}
          anchorEl={postPosPopper}
          placement="right"
          disablePortal
        >
          <ClickAwayListener onClickAway={() => setPostPosPopper(null)}>
            <ControlPopper
              hideServices={hideServices}
              hideProducts={hideProducts}
              hideNewPosition={hideNewPosition}
              hideDiscount={hideDiscount}
              hideTimeBilling={hideTimeBilling}
              onNewPosition={onNewPosition}
              onSelectProduct={onSelectProduct}
              onSelectService={onSelectService}
              onNewPercent={onNewPercent}
              onNewTimeBilling={onNewTimeBilling}
              onClose={() => setPostPosPopper(null)}
            />
          </ClickAwayListener>
        </Popper>
      </td>
    </tr>
  );
};

const ControlPopper = forwardRef((props, ref) => {
  const { t } = useTranslation();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const {
    hideServices,
    hideProducts,
    hideNewPosition,
    hideDiscount,
    hideTimeBilling,
    onNewPosition,
    onSelectProduct,
    onSelectService,
    onNewPercent,
    onNewTimeBilling,
    onClose,
  } = props;

  return (
    <Box
      ref={ref}
      className="flex flex-row gap-0"
      sx={{
        position: 'relative',
        overflowX: 'hidden',
        transition: 'width 0.3s ease',
        paddingLeft: '5px',
      }}
    >
      <Box
        className="flex flex-col gap-1 px-3 py-3 rounded-md"
        sx={{
          position: 'relative',
          background: `linear-gradient(135deg,  ${colors.glassFrom}, ${colors.glassTo})`,
          border: `1px solid ${colors.selected}`,
          transform: 'translate(-5px, 0px)',
          flexShrink: 0,
          maxHeight: '90vh',
          backdropFilter: `blur(5px)`,
        }}
      >
        <Typography className="w-full flex justify-center">
          {t('After subtotal')}
        </Typography>
        <Box className="flex flex-row">
          {!hideNewPosition && (
            <Tooltip title={t('Add Custom Position')} arrow disableInteractive>
              <IconButton
                onClick={() => {
                  onNewPosition(true);
                  onClose();
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
          {hasPermissions('product', 'read') && !hideProducts && (
            <Tooltip title={t('Add Product Position')} arrow disableInteractive>
              <IconButton
                onClick={() => {
                  onSelectProduct(true);
                  onClose();
                }}
              >
                <InventoryIcon />
              </IconButton>
            </Tooltip>
          )}
          {hasPermissions('service', 'read') && !hideServices && (
            <Tooltip title={t('Add Service Position')} arrow disableInteractive>
              <IconButton
                onClick={() => {
                  onSelectService(true);
                  onClose();
                }}
              >
                <SupervisedUserCircleIcon />
              </IconButton>
            </Tooltip>
          )}
          {!hideDiscount && (
            <Tooltip
              title={t('Add Percentage Position')}
              arrow
              disableInteractive
            >
              <IconButton
                onClick={() => {
                  onNewPercent(true);
                  onClose();
                }}
              >
                <PercentIcon />
              </IconButton>
            </Tooltip>
          )}
          {!hideTimeBilling && (
            <Tooltip title={t('Time Billing')} disableInteractive>
              <IconButton
                onClick={() => {
                  onNewTimeBilling(true);
                  onClose();
                }}
              >
                <AccessTimeIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
});

export default Controls;
